// Generic translation adapter utility
// This utility helps features extract translatable fields and apply translations in a reusable, maintainable way.
import type { TranslatableEvent, TranslatableTarget } from '@/types/translation';

/**
 * Collects translatable fields from a list of events for translation.
 * Returns a mapping of translation keys to original strings.
 */
export function collectTranslatableFields(
	target: TranslatableTarget,
	events: TranslatableEvent[],
	page: number,
): Record<string, string> {
	const toTranslate: Record<string, string> = {};
	events.forEach((event) => {
		if (event.name && !event.name.translated) {
			toTranslate[`${target.location}__${page}__${event.id}__${TRANSLATION_KEY.NAME}`] = event.name.original;
		}
		if (event.groupName && !event.groupName.translated) {
			toTranslate[`${target.location}__${page}__${event.id}__${TRANSLATION_KEY.GROUP_NAME}`] = event.groupName.original;
		}
		if (event.address && !event.address.translated) {
			toTranslate[`${target.location}__${page}__${event.id}__${TRANSLATION_KEY.ADDRESS}`] = event.address.original;
		}
	});
	return toTranslate;
}

/**
 * Applies translations to a list of events in-place.
 * Mutates the events array.
 */
export function applyTranslationsToEvents(
	target: TranslatableTarget,
	events: TranslatableEvent[],
	page: number,
	translations: Record<string, string>,
) {
	events.forEach((event) => {
		const nameKey = `${target.location}__${page}__${event.id}__${TRANSLATION_KEY.NAME}`;
		if (translations[nameKey]) {
			event.name.translated = translations[nameKey];
		}
		if (event.groupName) {
			const groupNameKey = `${target.location}__${page}__${event.id}__${TRANSLATION_KEY.GROUP_NAME}`;
			if (translations[groupNameKey]) {
				event.groupName.translated = translations[groupNameKey];
			}
		}
		const addressKey = `${target.location}__${page}__${event.id}__${TRANSLATION_KEY.ADDRESS}`;
		if (translations[addressKey]) {
			event.address.translated = translations[addressKey];
		}
	});
}
